<script setup>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import {
  TOPBAR_SLOT_ID,
  isTopbarSlotOccupied,
} from 'dashboard/composables/useTopbarSlot';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentAccount } = useAccount();

// Monitor-style page title derived from the top-level section in the route path.
const SECTION_LABELS = {
  dashboard: 'TOPBAR.SECTIONS.OVERVIEW',
  conversations: 'TOPBAR.SECTIONS.CONVERSATIONS',
  contacts: 'TOPBAR.SECTIONS.CONTACTS',
  reports: 'TOPBAR.SECTIONS.REPORTS',
  campaigns: 'TOPBAR.SECTIONS.CAMPAIGNS',
  settings: 'TOPBAR.SECTIONS.SETTINGS',
  notifications: 'TOPBAR.SECTIONS.NOTIFICATIONS',
  'power-apps': 'TOPBAR.SECTIONS.POWERAPPS',
  powerapps: 'TOPBAR.SECTIONS.POWERAPPS',
  // FLOW-06: a aba PowerFlow, que a regra de baixo escreveria "Powerflow".
  powerflow: 'TOPBAR.SECTIONS.POWERFLOW',
};

const pageTitle = computed(() => {
  const match = route.path.match(/\/accounts\/\d+\/([^/]+)/);
  const segment = match?.[1] || 'dashboard';
  const key = SECTION_LABELS[segment];
  if (key) return t(key);
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
});

const subtitle = computed(() => currentAccount.value?.name || '');

const isDark = ref(document.body.classList.contains('dark'));

const toggleTheme = () => {
  const next = document.body.classList.contains('dark') ? 'light' : 'dark';
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, next);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
  isDark.value = document.body.classList.contains('dark');
};

const openSearch = () => router.push({ name: 'search' });
const openProfileSettings = () =>
  router.push({ name: 'profile_settings_index' });
</script>

<template>
  <header
    class="sticky top-0 z-20 flex shrink-0 gap-4 justify-between items-center px-4 h-14 border-b backdrop-blur-lg border-n-weak bg-[rgb(var(--background-color)/0.72)]"
  >
    <!-- Pages with a header of their own teleport it in here (see useTopbarSlot). -->
    <div
      :id="TOPBAR_SLOT_ID"
      class="flex items-center h-full min-w-0"
      :class="isTopbarSlotOccupied ? 'flex-1' : 'hidden'"
    />
    <div v-show="!isTopbarSlotOccupied" class="min-w-0">
      <h1
        class="text-lg font-bold leading-tight truncate text-n-slate-12 tracking-[-0.01em]"
      >
        {{ pageTitle }}
      </h1>
      <p v-if="subtitle" class="text-xs leading-tight truncate text-n-slate-10">
        {{ subtitle }}
      </p>
    </div>
    <div class="flex gap-2 items-center shrink-0">
      <button
        type="button"
        :title="t('TOPBAR.SEARCH')"
        :aria-label="t('TOPBAR.SEARCH')"
        class="grid transition-all size-9 place-content-center rounded-[10px] border border-n-weak bg-n-solid-2 text-n-slate-11 hover:text-n-brand hover:border-n-brand hover:-translate-y-px"
        @click="openSearch"
      >
        <span class="size-4 i-lucide-search" />
      </button>
      <button
        type="button"
        :title="t('TOPBAR.TOGGLE_THEME')"
        :aria-label="t('TOPBAR.TOGGLE_THEME')"
        class="grid transition-all size-9 place-content-center rounded-[10px] border border-n-weak bg-n-solid-2 text-n-slate-11 hover:text-n-brand hover:border-n-brand hover:-translate-y-px"
        @click="toggleTheme"
      >
        <span v-if="isDark" class="size-4 i-lucide-sun" />
        <span v-else class="size-4 i-lucide-moon" />
      </button>
      <button
        type="button"
        :title="t('TOPBAR.PROFILE_SETTINGS')"
        :aria-label="t('TOPBAR.PROFILE_SETTINGS')"
        class="grid transition-all size-9 place-content-center rounded-[10px] border border-n-weak bg-n-solid-2 text-n-slate-11 hover:text-n-brand hover:border-n-brand hover:-translate-y-px"
        @click="openProfileSettings"
      >
        <span class="size-4 i-lucide-user-cog" />
      </button>
    </div>
  </header>
</template>
